import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FormEventHandler, FunctionComponent, PropsWithChildren } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "router";
import { Footer } from "../Footer";
import { useSetExpandManualLocationAtom } from "../location/expandManualLocationAtom";
import { useMetadataAtomValue } from "../metadataAtom";
import { rateMapper } from "../pricing/constants";
import { useSidebarAtomValue } from "../sidebar/sidebarAtom";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { useFooterNavigation } from "../useFooterNavigation";
import { formInvalidHandler } from "./formInvalidHandler";
import { useFormHandler } from "./useFormHandler";

interface Props extends PropsWithChildren {
    data: PropertyData;
}

export const PropertyForm: FunctionComponent<Props> = ({ data: propertyData, children }) => {
    const {
        cmsData: {
            common: { back, next },
            publish: { publish },
        },
    } = useStaticDataAtomValue();
    const { isInternalUser } = useMetadataAtomValue();
    const sidebarAtomValue = useSidebarAtomValue();
    const { pathname } = useDynamicLocation();
    const { propertyId } = useParams("/:lang/list-my-property/onboard/:propertyId");
    const form = useForm<PropertyData>({
        values: propertyData,
        shouldFocusError: true,
    });
    const contractId = useWatch({
        control: form.control,
        name: "contractId",
    });
    const { mutate, isLoading } = useFormHandler();
    const setExpandManualLocationAtom = useSetExpandManualLocationAtom();
    const { handleBack } = useFooterNavigation();

    const isProfilePage = pathname === "/:lang/list-my-property/onboard/:propertyId/profile";
    const isPublishPage = pathname === "/:lang/list-my-property/onboard/:propertyId/publish";

    const handleSubmit = (data: PropertyData) => {
        if (!isProfilePage)
            return mutate({
                ...data,
                nightlyRate: typeof data.nightlyRate === "string" ? Number(data.nightlyRate) : data.nightlyRate,
                weeklyDiscount: data.rate === rateMapper.ChannelManager ? null : data.weeklyDiscount,
                monthlyDiscount: data.rate === rateMapper.ChannelManager ? null : data.monthlyDiscount,
            });
    };

    const formSubmitEvent: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        const formSubmitHandler = form.handleSubmit(handleSubmit, formInvalidHandler("property", propertyId, pathname));

        // if user landed on location page, expand fields first then call react-hook-form
        if (pathname === "/:lang/list-my-property/onboard/:propertyId/location") {
            setExpandManualLocationAtom(true);
            setTimeout(() => {
                return formSubmitHandler();
            }, 1);
        } else {
            return formSubmitHandler();
        }
    };

    return (
        <FormProvider {...form}>
            <form id="property-data-form" onSubmit={formSubmitEvent}>
                {children}
                {!isProfilePage && (
                    <Footer
                        secondary={{
                            type: "button",
                            label: back,
                            disabled: isLoading || sidebarAtomValue.length === 0,
                            dataElementName: "list-your-property-back",
                        }}
                        primary={{
                            type: "submit",
                            label: isPublishPage ? publish : next,
                            disabled:
                                isLoading ||
                                (isPublishPage && (isInternalUser || contractId === null || contractId.length === 0)),
                            loading: isLoading,
                            dataElementName: "list-your-property-next",
                        }}
                        onSecondary={handleBack}
                    />
                )}
            </form>
        </FormProvider>
    );
};
